import { parseTransactionIds } from "helpers/transactionIdFilter";
import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "store/index";

import {
  getOperationsList,
  handlePaginate,
} from "store/payments/operations/slice";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { operationsListLimit, operationsListLimits } from "configs/limits";
import OperationsFilters from "./components/OperationsFilters";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import { useLocation, useNavigate } from "react-router-dom";
import { t } from "i18next";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";

const compactReference = (value: string, startLength = 6, endLength = 4) => {
  if (value.length <= startLength + endLength + 3) return value;

  return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
};

const OperationsList: React.FC = () => {
  const operationsList = useAppSelector(
    (store) => store.paymentsOperations.list,
  );

  const isListFetched = useAppSelector(
    (store) => store.paymentsOperations.isListFetched,
  );

  const totalItems = useAppSelector(
    (store) => store.paymentsOperations.totalItems,
  );

  const totalPages = useAppSelector(
    (store) => store.paymentsOperations.totalPages,
  );

  const activePage = useAppSelector(
    (store) => store.paymentsOperations.activePage,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'paymentsOperations',
    isListFetched
  });

  const [operationId, setOperationId] = useState("");
  const [userId, setUserId] = useState("");

  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);

  const [reasons, setReasons] = useState<
    { value: string | number; label: string }[]
  >([]);

  const navigate = useNavigate();
  const location = useLocation();
  const activeOperationId = new URLSearchParams(location.search).get(
    "operationId",
  );

  const dispatch = useAppDispatch();
  const [currentLimit, setCurrentLimit] = useState(operationsListLimit);

  const [order, setOrder] = useState<{
    id: string;
    amount: string;
    currency: string;
    statusCode: string;
    type: string;
  }>({
    id: "none",
    amount: "none",
    currency: "none",
    statusCode: "none",
    type: "none",
  });

  const [types, setTypes] = useState<
    { value: string | number; label: string }[]
  >([]);

  const [statuses, setStatuses] = useState<
    { value: string | number; label: string }[]
  >([]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getOperationsList({
          limit: currentLimit,
          offset: activePage - 1,

          operationId: operationId ? parseTransactionIds(operationId) : undefined,
          userIds: userId ? parseTransactionIds(userId) : undefined,

          currencies: currencies.map((el) => el.value as string),
          operationTypes: types.map((el) => el.value as string),
          operationReasonCodes: reasons.map((el) => el.value as string),
          operationStatusCodes: statuses.map((el) => el.value as string),

          order,
        }),
      );
    }
  }, [activePage, currentLimit, dispatch, isListFetched, order]);

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };

    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleRowClick = (id: number) => {
    navigate(`${location.pathname}?operationId=${id}`);
    dispatch(openModal({ activeModal: ModalTypes.operationsDetails }));
  };

  const tableData = operationsList.map((item) => ({
    ...item,
    type: item.origin === "backoffice_manual" ? `${item.type} · ${t("manual")}` : item.type,
    user: item.user?.email || item.user?.firstName,
  }));

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <OperationsFilters
        operationId={operationId}
        setOperationId={setOperationId}
        userId={userId}
        setUserId={setUserId}
        selectedCurrencies={currencies}
        handleUpdateCurrencies={(e) => setCurrencies(e)}
        selectedReasons={reasons}
        handleUpdateReasons={(e) => setReasons(e)}
        selectedTypes={types}
        handleUpdateTypes={(e) => setTypes(e)}
        selectedStatuses={statuses}
        handleUpdateStatuses={(e) => setStatuses(e)}
        handleApply={handleApply}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={11} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {operationsList.length === 0 && (
              <EmptyState message={t("noOperationtTransactionsFound")} />
            )}

            {operationsList.length > 0 && (
              <Table
                data={tableData}
                idField="id"
                order={order}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                hasCheckbox={false}
                hasOptions
                hasView
                hasEdit={false}
                hasDelete={false}
                onView={handleRowClick}
                onEditByRowClick={handleRowClick}
                customColumns={(row) => ({
                  publicId: row.publicId ? (
                    <div className="copyable-title-cell">
                      <CopyToClipboard text={row.publicId} />
                      <span title={row.publicId}>
                        {compactReference(row.publicId)}
                      </span>
                    </div>
                  ) : (
                    <span>—</span>
                  ),
                  externalRef: row.externalRef ? (
                    <div className="copyable-title-cell">
                      <CopyToClipboard text={row.externalRef} />
                      <span title={row.externalRef}>
                        {compactReference(row.externalRef)}
                      </span>
                    </div>
                  ) : (
                    <span>—</span>
                  ),
                })}
                activeRowId={
                  activeOperationId ? Number(activeOperationId) : null
                }
                className="operations-table"
              />
            )}
          </div>
        )}

        {isListFetched && operationsList.length > 0 && (
          <Pagination
            limitsArray={operationsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default OperationsList;
